import type { StatModifier } from '../stats/StatSheet';

/**
 * The pickup-fed stat track (doc §2): collection grants small stat bumps with
 * no meter, no pause, no cards. Bumps are threshold crossings of gemsEarned —
 * a pure function of the monotonic total, so grants are order-independent and
 * consume ZERO rng of any kind (the strongest determinism answer available).
 *
 * Kills feed the bar (picks); collection feeds stats. A player who ignores
 * drops to chase kills gets picks faster and stays fragile — that split is
 * the design's central trade.
 */

/** Cumulative gemsEarned needed for bump N (1-based). Quadratic growth is the
 *  diminishing-returns curve; each bump itself stays feelable. */
export function bumpThreshold(n: number): number {
  return Math.round(40 * n + 10 * n * n);
}

/** Hard cap — bounds degenerate farming; ~a full strong run's worth. */
export const PICKUP_BUMP_CAP = 30;

/**
 * (tuning) Chance a bump also mends 1 HP. The roll happens World-side on
 * gameplayRng (collection timing is reactive) — the bump TABLE below stays
 * rng-free: which stats you get is still a pure function of gemsEarned.
 */
export const PICKUP_HEAL_CHANCE = 0.15;

/** How many bumps a given (pickupRateMult-scaled) gem total has crossed. */
export function pickupBumpsEarned(effectiveGems: number): number {
  let n = 0;
  while (n < PICKUP_BUMP_CAP && effectiveGems >= bumpThreshold(n + 1)) n++;
  return n;
}

/**
 * Fixed rotation — bump k grants entry k mod length. No rng anywhere.
 * gemGainMult is deliberately absent (gems→more gems self-feeds and inflates
 * the tithe); projSpeedMult is absent as the least feelable stat (doc §5).
 * magnetRange stays: pickup range as a live build axis is the point.
 */
export const PICKUP_BUMP_TABLE: readonly (StatModifier & { label: string })[] = [
  { stat: 'damageMult', pct: 0.02, label: '+2% damage' },
  { stat: 'moveSpeed', flat: 4, label: '+4 speed' },
  { stat: 'cooldownMult', pct: -0.015, label: '−1.5% cooldowns' },
  { stat: 'areaMult', pct: 0.02, label: '+2% area' },
  { stat: 'magnetRange', flat: 8, label: '+8 magnet' },
  { stat: 'maxHp', flat: 1, label: '+1 max HP' },
];
